import { useState, useEffect } from "react";
import api from "../../services/api";
import ProviderPager from "../../components/provider/ProviderPager";
import { filterProblem } from "../../components/provider/providerSearchRules";
import ProviderOrganizationsEditor from "../../components/provider/ProviderOrganizationsEditor";
import LinkedAccountsPanel from "../../components/provider/LinkedAccountsPanel";
import { useAuth0 } from "../../hooks/useAuth0";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,opsz,wght@0,9..144,600&display=swap');
  .pa-root { font-family: 'DM Sans', system-ui, sans-serif; }
  @keyframes paFadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes paSpin   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  .pa-row { transition: background 0.15s; }
  .pa-row:hover { background: #f8faff !important; }
  .pa-input { transition: border-color 0.2s; }
  .pa-input:focus { border-color: #1A5EA8 !important; outline: none; box-shadow: 0 0 0 3px rgba(102,126,234,0.12); }
  .pa-btn { transition: all 0.2s; }
  .pa-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.08); }
  .pa-toggle { cursor: pointer; transition: all 0.2s; }
`;

// ── Single source of truth for grid columns ──────────────────────────────────
const GRID = "2fr 2fr 2fr 1fr 180px 160px 110px";

const MEDICAL_ADVISORS = [
  { name: "Dr. Nitasha Sarswat", email: "nsarswat@bsd.uchicago.edu", title: "Chief Medical Advisor — NephroTrax" },
];

const TIERS = [
  { value: 0, label: "None",               color: "#9ca3af" },
  { value: 1, label: "Verified Specialist", color: "#0B2545" },
  { value: 2, label: "Relevant Specialist", color: "#1B6CA8" },
];

function Spinner({ size = 18 }) {
  return <div style={{ width: size, height: size, borderRadius: "50%",
    border: "2px solid #e5e7eb", borderTopColor: "#1A5EA8",
    animation: "paSpin 0.7s linear infinite", flexShrink: 0 }} />;
}

function TierBadge({ tier }) {
  const t = TIERS.find(t => t.value === tier) || TIERS[0];
  if (!tier || tier === 0) return null;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px",
      borderRadius: 999, background: tier === 1 ? "#f5f3ff" : "#eef2ff",
      color: t.color, border: `1px solid ${t.color}30` }}>
      {t.label}
    </span>
  );
}

// The API names this flag nephrologySpecialist. Older builds called it amyloidSpecialist, so read either one.
const isNephrologist = (p) => Boolean(p && (p.nephrologySpecialist ?? p.amyloidSpecialist));

export default function ProviderAdminPage() {
  const { user, isAuthenticated } = useAuth0();

  const [search,    setSearch]    = useState({ firstName: "", lastName: "", npi: "", specialty: "", state: "" });
  const [nephOnly,  setNephOnly]  = useState(false);
  const [pageInfo,  setPageInfo]  = useState(null);   // { page, size, total, totalPages } of the last search
  const [searched,  setSearched]  = useState(false);
  const [providers, setProviders] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState({});
  const [editing,   setEditing]   = useState(null);
  const [editForm,  setEditForm]  = useState({});
  const [expanded,  setExpanded]  = useState(null);
  const [toast,     setToast]     = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  const [showAdd,   setShowAdd]   = useState(false);
  const [addForm,   setAddForm]   = useState({
    npi: "", firstName: "", lastName: "", credential: "",
    specialty: "", groupName: "", addressLine1: "",
    city: "", state: "", zip: "", phone: "",
    nephrologySpecialist: false, amyloidSpecialist: false, specialistTier: 1,
    verifiedBy: MEDICAL_ADVISORS[0].name, specialistNotes: ""
  });

  useEffect(() => { setTimeout(() => setAnimateIn(true), 100); }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // The page no longer loads every specialist when it opens (about ten thousand rows rendered at once froze
  // the browser). Search with at least one filter instead; results come one page at a time.

  // ── Search ────────────────────────────────────────────────────────────────
  // ── Search: needs at least one filter; results come one page at a time ─────
  const PAGE_SIZE = 25;
  const canSearch = filterProblem(search, nephOnly) === null;

  const runSearch = async (pageNum) => {
    const problem = filterProblem(search, nephOnly);
    if (problem) { showToast(problem, "error"); return; }
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await api.get("/v1/admin/providers/page", {
        params: {
          firstName: search.firstName.trim() || undefined,
          lastName:  search.lastName.trim()  || undefined,
          npi:       search.npi.trim()       || undefined,
          specialty: search.specialty.trim() || undefined,
          state:     search.state.trim()     || undefined,
          nephrologyOnly: nephOnly || undefined,
          page: pageNum,
          size: PAGE_SIZE,
        }
      });
      const data = res.data || {};
      setProviders(data.items || []);
      setPageInfo({ page: data.page ?? pageNum, size: data.size ?? PAGE_SIZE, total: data.total ?? 0, totalPages: data.totalPages ?? 0 });
      setSearched(true);
      if ((data.items || []).length === 0) showToast("No providers found", "info");
    } catch (err) {
      showToast(err.response?.data?.error || "Search failed", "error");
    } finally { setLoading(false); }
  };
  const doSearch = () => runSearch(0);
  const goToPage = (n) => runSearch(n);

  // ── Update specialist status ──────────────────────────────────────────────
  const updateSpecialist = async (npi, updates) => {
    setSaving(s => ({ ...s, [npi]: true }));
    try {
      await api.put(`/v1/admin/providers/${npi}/specialist`, {
        ...updates, updatedBy: user?.email || user?.sub,
      });
      setProviders(prev => prev.map(p => p.npi === npi ? { ...p, ...updates } : p));
      if (updates.nephrologySpecialist) {
        const provider = providers.find(p => p.npi === npi);
        showToast(`✅ ${provider?.fullName || npi} flagged as a Nephrologist — notification sent`);
      } else {
        showToast("✅ Provider updated");
      }
    } catch { showToast("Failed to update provider", "error"); }
    finally   { setSaving(s => ({ ...s, [npi]: false })); }
  };

  // ── Save full edit ────────────────────────────────────────────────────────
  const saveEdit = async () => {
    setSaving(s => ({ ...s, [editing]: true }));
    try {
      await api.put(`/v1/admin/providers/${editing}`, {
        ...editForm, updatedBy: user?.email || user?.sub,
      });
      setProviders(prev => prev.map(p => p.npi === editing ? { ...p, ...editForm } : p));
      setEditing(null);
      showToast("✅ Provider saved");
    } catch { showToast("Failed to save", "error"); }
    finally   { setSaving(s => ({ ...s, [editing]: false })); }
  };

  // ── Add provider ──────────────────────────────────────────────────────────
  const addProvider = async () => {
    setSaving(s => ({ ...s, new: true }));
    try {
      await api.post("/v1/admin/providers", { ...addForm, updatedBy: user?.email || user?.sub });
      setShowAdd(false);
      showToast("✅ Provider added successfully");
      doSearch();
    } catch { showToast("Failed to add provider", "error"); }
    finally   { setSaving(s => ({ ...s, new: false })); }
  };

  // ── Delete provider ───────────────────────────────────────────────────────
  const deleteProvider = async (npi, name) => {
    if (!window.confirm(`Remove ${name} from the database?`)) return;
    try {
      await api.delete(`/v1/admin/providers/${npi}`);
      setProviders(prev => prev.filter(p => p.npi !== npi));
      showToast("✅ Provider removed");
    } catch { showToast("Failed to remove provider", "error"); }
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #e5e7eb",
    borderRadius: 8, fontSize: 13, fontFamily: "inherit",
    boxSizing: "border-box", background: "white"
  };
  const labelStyle = {
    fontSize: 10, fontWeight: 700, color: "#6b7280",
    textTransform: "uppercase", letterSpacing: "0.08em",
    display: "block", marginBottom: 4
  };

  return (
    <div className="pa-root" style={{ background: "#f8faff", minHeight: "100vh", paddingBottom: 80 }}>
      <style>{STYLES}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 9999,
          padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600,
          background: toast.type === "error" ? "#fef2f2" : toast.type === "info" ? "#eff6ff" : "#f0fdf4",
          border: `1px solid ${toast.type === "error" ? "#fca5a5" : toast.type === "info" ? "#bfdbfe" : "#86efac"}`,
          color: toast.type === "error" ? "#dc2626" : toast.type === "info" ? "#2563eb" : "#166534",
          boxShadow: "0 4px 20px rgba(0,0,0,0.12)", maxWidth: 420 }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#0D2B45,#1a3a5c,#2d1b69)",
        padding: "32px 24px", animation: animateIn ? "paFadeUp 0.4s ease both" : "none" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: 26, fontWeight: 700,
              color: "white", margin: "0 0 4px" }}>🏥 Provider Management</h1>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: 0 }}>
              Manage nephrologists · Badges approved by Dr. Nitasha Sarswat
            </p>
          </div>
          <button className="pa-btn" onClick={() => setShowAdd(true)}
            style={{ padding: "10px 20px", background: "linear-gradient(135deg,#1A5EA8,#0B2545)",
              color: "white", border: "none", borderRadius: 10, fontSize: 13,
              fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            + Add Provider
          </button>
        </div>
      </div>

      <div style={{ padding: "24px 20px" }}>

        {/* Search panel */}
        <div style={{ background: "white", borderRadius: 16, padding: "20px 24px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 20,
          animation: animateIn ? "paFadeUp 0.4s ease 0.1s both" : "none" }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            {/* First Name */}
            <div style={{ flex: "1 1 140px" }}>
            <label style={labelStyle}>First Name</label>
            <input className="pa-input" style={inputStyle} placeholder="e.g. Nitasha"
                value={search.firstName}
                onChange={e => setSearch(s => ({ ...s, firstName: e.target.value }))}
                onKeyPress={e => e.key === "Enter" && doSearch()} />
            </div>

            {/* Last Name */}
            <div style={{ flex: "1 1 140px" }}>
            <label style={labelStyle}>Last Name</label>
            <input className="pa-input" style={inputStyle} placeholder="e.g. Sarswat"
                value={search.lastName}
                onChange={e => setSearch(s => ({ ...s, lastName: e.target.value }))}
                onKeyPress={e => e.key === "Enter" && doSearch()} />
            </div>
            <div style={{ flex: "1 1 120px" }}>
              <label style={labelStyle}>NPI</label>
              <input className="pa-input" style={inputStyle} placeholder="e.g. 1033498167"
                value={search.npi}
                onChange={e => setSearch(s => ({ ...s, npi: e.target.value }))}
                onKeyPress={e => e.key === "Enter" && doSearch()} />
            </div>
            <div style={{ flex: "1 1 160px" }}>
              <label style={labelStyle}>Specialty</label>
              <input className="pa-input" style={inputStyle} placeholder="e.g. Cardiology"
                value={search.specialty}
                onChange={e => setSearch(s => ({ ...s, specialty: e.target.value }))}
                onKeyPress={e => e.key === "Enter" && doSearch()} />
            </div>
            <div style={{ flex: "0 1 80px" }}>
              <label style={labelStyle}>State</label>
              <input className="pa-input" style={inputStyle} placeholder="e.g. IL"
                value={search.state}
                onChange={e => setSearch(s => ({ ...s, state: e.target.value.toUpperCase() }))}
                onKeyPress={e => e.key === "Enter" && doSearch()} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#374151", cursor: "pointer", whiteSpace: "nowrap", paddingBottom: 9 }}>
              <input type="checkbox" checked={nephOnly} onChange={e => setNephOnly(e.target.checked)} />
              Nephrology only
            </label>
            <button className="pa-btn" onClick={doSearch} disabled={loading || !canSearch} title={filterProblem(search, nephOnly) || "Search the provider directory"}
              style={{ opacity: canSearch ? 1 : 0.55, padding: "9px 20px",
                background: loading ? "rgba(102,126,234,0.35)" : "linear-gradient(135deg,#1A5EA8,#0B2545)",
                color: "white", border: "none", borderRadius: 8, fontSize: 13,
                fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8 }}>
              {loading ? <Spinner size={14} /> : "🔍"} Search
            </button>
          </div>
        </div>

        {/* Results table */}
        <div style={{ background: "white", borderRadius: 16, overflow: "hidden",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          animation: animateIn ? "paFadeUp 0.4s ease 0.2s both" : "none" }}>

          {/* Table title */}
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #f3f4f6",
            display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 600,
              color: "#0D2B45", margin: 0 }}>
              Providers {pageInfo && pageInfo.total > 0 && `(${pageInfo.total.toLocaleString()} found)`}
            </h2>
            {providers.filter(p => isNephrologist(p)).length > 0 && (
              <span style={{ fontSize: 12, fontWeight: 600, color: "#0B2545" }}>
                🧬 {providers.filter(isNephrologist).length} {providers.filter(isNephrologist).length === 1 ? "Nephrologist" : "Nephrologists"}
              </span>
            )}
          </div>

          {pageInfo && pageInfo.total > 0 && (
            <ProviderPager pageInfo={pageInfo} disabled={loading} onPage={goToPage} />
          )}

          {/* Column headers — using GRID constant */}
          <div style={{ display: "grid", gridTemplateColumns: GRID,
            padding: "10px 20px", background: "#0D2B45",
            fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.6)",
            textTransform: "uppercase", letterSpacing: "0.08em" }}>
            <div>Provider</div>
            <div>Institution</div>
            <div>Specialty</div>
            <div>Location</div>
            <div>Nephrologist</div>
            <div>Verified By</div>
            <div>Actions</div>
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 40, gap: 12 }}>
              <Spinner size={28} />
              <span style={{ fontSize: 13, color: "#9ca3af" }}>Loading providers...</span>
            </div>
          )}

          {/* Empty */}
          {!loading && providers.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🧬</div>
              <p style={{ fontSize: 14, fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>
                {searched ? "No providers match those filters" : "Search the provider directory"}
              </p>
              <p style={{ fontSize: 13, margin: 0 }}>
                {searched ? "Try fewer or different filters." : "Enter at least one filter (a name, NPI, specialty or state, or tick Nephrology only), then press Search. Results appear 25 at a time."}
              </p>
            </div>
          )}

          {/* Rows — using GRID constant */}
          {!loading && providers.map((p, i) => (
            <div key={p.npi || i}>

              {/* Main row */}
              <div className="pa-row" style={{
                display: "grid", gridTemplateColumns: GRID,
                padding: "14px 20px", borderBottom: "1px solid #f9fafb",
                alignItems: "center", background: i % 2 === 0 ? "#fafafa" : "white" }}>

                {/* Provider */}
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "#0D2B45",
                    margin: "0 0 2px", overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.fullName || `${p.firstName || ""} ${p.lastName || ""}`.trim()}
                  </p>
                  <p style={{ fontSize: 11, color: "#9ca3af", margin: 0, fontFamily: "monospace" }}>
                    NPI: {p.npi}
                  </p>
                </div>

                {/* Institution */}
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 12, color: "#374151", margin: 0, fontWeight: 500,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.groupName || "—"}
                  </p>
                </div>

                {/* Specialty — full text, wraps */}
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 12, color: "#374151", margin: 0,
                    lineHeight: 1.4, wordBreak: "break-word" }}>
                    {p.specialty || "—"}
                  </p>
                </div>

                {/* Location */}
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {p.city}, {p.state}
                </div>

                {/* Nephrologist toggle */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {saving[p.npi] ? <Spinner size={14} /> : (
                      <div className="pa-toggle"
                        onClick={() => updateSpecialist(p.npi, {
                          nephrologySpecialist: !isNephrologist(p),
                          amyloidSpecialist:    !isNephrologist(p),
                          specialistTier:    !isNephrologist(p) ? 1 : 0,
                          verifiedBy:        p.verifiedBy || MEDICAL_ADVISORS[0].name,
                          specialistNotes:   p.specialistNotes || ""
                        })}
                        style={{ width: 36, height: 20, borderRadius: 999, flexShrink: 0,
                          background: isNephrologist(p)
                            ? "linear-gradient(135deg,#1A5EA8,#0B2545)" : "#e5e7eb",
                          position: "relative", transition: "all 0.2s" }}>
                        <div style={{ position: "absolute", top: 2,
                          left: isNephrologist(p) ? 18 : 2,
                          width: 16, height: 16, borderRadius: "50%",
                          background: "white", transition: "left 0.2s",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
                      </div>
                    )}
                    <span style={{ fontSize: 11, fontWeight: 600,
                      color: isNephrologist(p) ? "#0B2545" : "#9ca3af" }}>
                      {isNephrologist(p) ? "🧬 Yes" : "No"}
                    </span>
                  </div>
                  <TierBadge tier={p.specialistTier} />
                </div>

                {/* Verified by */}
                <div style={{ fontSize: 11, color: "#0B2545", fontWeight: 600,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.verifiedBy || "—"}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 5, flexWrap: "nowrap", alignItems: "center" }}>
                  <button className="pa-btn"
                    onClick={() => { setEditing(p.npi); setEditForm({ ...p }); setExpanded(null); }}
                    style={{ padding: "4px 8px", background: "#eef2ff", color: "#1A5EA8",
                      border: "1px solid #c7d2fe", borderRadius: 6, fontSize: 11,
                      fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                      whiteSpace: "nowrap" }}>
                    Edit
                  </button>
                  <button className="pa-btn"
                    onClick={() => deleteProvider(p.npi, p.fullName || p.lastName)}
                    style={{ padding: "4px 8px", background: "#fef2f2", color: "#dc2626",
                      border: "1px solid #fca5a5", borderRadius: 6, fontSize: 11,
                      fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    ✕
                  </button>
                  <button className="pa-btn"
                    onClick={() => { setExpanded(expanded === p.npi ? null : p.npi); setEditing(null); }}
                    style={{ padding: "4px 8px", background: "#f0fdf4", color: "#059669",
                      border: "1px solid #86efac", borderRadius: 6, fontSize: 11,
                      fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    {expanded === p.npi ? "▲" : "▼"}
                  </button>
                </div>
              </div>

              {/* ── Expanded detail panel ─────────────────────────────── */}
              {expanded === p.npi && (
                <div style={{ padding: "20px 24px", background: "#f8faff",
                  borderBottom: "2px solid #e0e7ff", borderLeft: "4px solid #059669" }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: "#059669",
                    textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 16px" }}>
                    Full Provider Record
                  </p>
                  <div style={{ display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
                    {[
                      ["NPI",                p.npi],
                      ["First Name",         p.firstName],
                      ["Last Name",          p.lastName],
                      ["Credential",         p.credential],
                      ["Full Specialty",     p.specialty],
                      ["Secondary Specialty",p.secondarySpecialty],
                      ["Group / Institution",p.groupName],
                      ["Address",            p.addressLine1],
                      ["City",               p.city],
                      ["State",              p.state],
                      ["Zip",                p.zip],
                      ["Phone",              p.phone],
                      ["Gender",             p.gender],
                      ["Medical School",     p.medicalSchool],
                      ["Graduation Year",    p.graduationYear],
                      ["Latitude",           p.latitude],
                      ["Longitude",          p.longitude],
                      ["Nephrologist", isNephrologist(p) ? "✅ Yes" : "❌ No"],
                      ["Specialist Tier",    p.specialistTier === 1 ? "Verified" : p.specialistTier === 2 ? "Relevant" : "None"],
                      ["Verified By",        p.verifiedBy],
                      ["Verified At",        p.verifiedAt ? new Date(p.verifiedAt).toLocaleString() : "—"],
                      ["Notes",              p.specialistNotes],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <p style={{ fontSize: 10, fontWeight: 700, color: "#1A5EA8",
                          textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 3px" }}>
                          {label}
                        </p>
                        <p style={{ fontSize: 12, color: "#374151", margin: 0,
                          wordBreak: "break-word", lineHeight: 1.5 }}>
                          {value != null && value !== "" ? String(value) : "—"}
                        </p>
                      </div>
                    ))}
                  </div>
                  {p.cmsProfileUrl && (
                    <div style={{ marginTop: 16 }}>
                      <a href={p.cmsProfileUrl} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 12, color: "#1A5EA8", fontWeight: 600 }}>
                        View CMS Profile ↗
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* ── Edit panel ───────────────────────────────────────── */}
              {editing === p.npi && (
                <div style={{ padding: "20px 24px", background: "#f0f4ff",
                  borderBottom: "2px solid #e0e7ff", borderLeft: "4px solid #1A5EA8" }}>
                  <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 15, fontWeight: 600,
                    color: "#0D2B45", margin: "0 0 16px" }}>
                    Editing — {p.fullName || p.lastName}
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)",
                    gap: 12, marginBottom: 12 }}>
                    {[
                      ["First Name",        "firstName"],
                      ["Last Name",         "lastName"],
                      ["Credential",        "credential"],
                      ["Specialty",         "specialty"],
                      ["Group/Institution", "groupName"],
                      ["Address",           "addressLine1"],
                      ["City",              "city"],
                      ["State",             "state"],
                      ["Zip",               "zip"],
                      ["Phone",             "phone"],
                    ].map(([label, field]) => (
                      <div key={field}>
                        <label style={labelStyle}>{label}</label>
                        <input className="pa-input" style={inputStyle}
                          value={editForm[field] || ""}
                          onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))} />
                      </div>
                    ))}
                    <div>
                      <label style={labelStyle}>Specialist Tier</label>
                      <select className="pa-input" style={{ ...inputStyle, background: "white" }}
                        value={editForm.specialistTier || 0}
                        onChange={e => setEditForm(f => ({ ...f, specialistTier: Number(e.target.value) }))}>
                        {TIERS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Verified By</label>
                      <select className="pa-input" style={{ ...inputStyle, background: "white" }}
                        value={editForm.verifiedBy || ""}
                        onChange={e => setEditForm(f => ({ ...f, verifiedBy: e.target.value }))}>
                        <option value="">— Select —</option>
                        {MEDICAL_ADVISORS.map(a => (
                          <option key={a.name} value={a.name}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Notes</label>
                    <textarea className="pa-input"
                      style={{ ...inputStyle, height: 72, resize: "vertical" }}
                      value={editForm.specialistNotes || ""}
                      onChange={e => setEditForm(f => ({ ...f, specialistNotes: e.target.value }))} />
                  </div>
                  <LinkedAccountsPanel npi={p.npi} />

                  <ProviderOrganizationsEditor npi={p.npi} primaryName={p.groupName} />

                  <div style={{ display: "flex", gap: 10 }}>
                    <button className="pa-btn" onClick={saveEdit} disabled={saving[p.npi]}
                      style={{ padding: "9px 20px",
                        background: "linear-gradient(135deg,#1A5EA8,#0B2545)",
                        color: "white", border: "none", borderRadius: 8, fontSize: 13,
                        fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                        display: "flex", alignItems: "center", gap: 8 }}>
                      {saving[p.npi] ? <Spinner size={13} /> : null} Save Changes
                    </button>
                    <button className="pa-btn" onClick={() => setEditing(null)}
                      style={{ padding: "9px 16px", background: "white", color: "#6b7280",
                        border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13,
                        fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Add Provider Modal ──────────────────────────────────────────── */}
        {showAdd && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20 }}>
            <div style={{ background: "white", borderRadius: 20, padding: "28px 28px",
              maxWidth: 700, width: "100%", maxHeight: "90vh", overflowY: "auto",
              boxShadow: "0 24px 80px rgba(0,0,0,0.3)" }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: 20 }}>
                <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 20, fontWeight: 700,
                  color: "#0D2B45", margin: 0 }}>Add New Provider</h2>
                <button onClick={() => setShowAdd(false)}
                  style={{ background: "none", border: "none", fontSize: 20,
                    cursor: "pointer", color: "#9ca3af" }}>✕</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                {[
                  ["NPI *",              "npi"],
                  ["First Name *",       "firstName"],
                  ["Last Name *",        "lastName"],
                  ["Credential",         "credential"],
                  ["Specialty *",        "specialty"],
                  ["Group/Institution",  "groupName"],
                  ["Address",            "addressLine1"],
                  ["City",               "city"],
                  ["State",              "state"],
                  ["Zip",                "zip"],
                  ["Phone",              "phone"],
                ].map(([label, field]) => (
                  <div key={field}>
                    <label style={labelStyle}>{label}</label>
                    <input className="pa-input" style={inputStyle}
                      value={addForm[field] || ""}
                      onChange={e => setAddForm(f => ({ ...f, [field]: e.target.value }))} />
                  </div>
                ))}
                <div>
                  <label style={labelStyle}>Specialist Tier</label>
                  <select className="pa-input" style={{ ...inputStyle, background: "white" }}
                    value={addForm.specialistTier}
                    onChange={e => setAddForm(f => ({ ...f, specialistTier: Number(e.target.value) }))}>
                    {TIERS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Verified By</label>
                  <select className="pa-input" style={{ ...inputStyle, background: "white" }}
                    value={addForm.verifiedBy}
                    onChange={e => setAddForm(f => ({ ...f, verifiedBy: e.target.value }))}>
                    <option value="">— Select —</option>
                    {MEDICAL_ADVISORS.map(a => (
                      <option key={a.name} value={a.name}>{a.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12,
                padding: "12px 16px", background: "#f8faff", borderRadius: 10, marginBottom: 12 }}>
                <div className="pa-toggle"
                  onClick={() => setAddForm(f => ({ ...f, nephrologySpecialist: !f.nephrologySpecialist, amyloidSpecialist: !f.nephrologySpecialist }))}
                  style={{ width: 40, height: 22, borderRadius: 999, flexShrink: 0,
                    background: addForm.nephrologySpecialist
                      ? "linear-gradient(135deg,#1A5EA8,#0B2545)" : "#e5e7eb",
                    position: "relative", transition: "all 0.2s" }}>
                  <div style={{ position: "absolute", top: 2,
                    left: addForm.nephrologySpecialist ? 20 : 2,
                    width: 18, height: 18, borderRadius: "50%", background: "white",
                    transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
                </div>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "#0D2B45", margin: "0 0 2px" }}>
                    🧬 Flag as Nephrologist
                  </p>
                  <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>
                    A notification email will be sent
                  </p>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Notes</label>
                <textarea className="pa-input" style={{ ...inputStyle, height: 72, resize: "vertical" }}
                  placeholder="Why is this provider flagged as a nephrologist?"
                  value={addForm.specialistNotes}
                  onChange={e => setAddForm(f => ({ ...f, specialistNotes: e.target.value }))} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="pa-btn" onClick={addProvider} disabled={saving.new}
                  style={{ padding: "10px 24px",
                    background: "linear-gradient(135deg,#1A5EA8,#0B2545)",
                    color: "white", border: "none", borderRadius: 10, fontSize: 13,
                    fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    display: "flex", alignItems: "center", gap: 8 }}>
                  {saving.new ? <Spinner size={13} /> : null} Add Provider
                </button>
                <button className="pa-btn" onClick={() => setShowAdd(false)}
                  style={{ padding: "10px 16px", background: "white", color: "#6b7280",
                    border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 13,
                    fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}